import React, { useMemo } from "react"
import EmptyDataMessage from "../../../../components/molecules/EmptyDataPieChartMessage.molecule"
import LineChart from "../../../../components/compounds/graphics/LineChart.compound"
import Tooltip from "../../../../components/atoms/Tooltip.atom"
import { gcdMultiple } from "../../../../utils/calc/generalCalcs.calc"
import { CONTAINER_OF_CHART_LOADING_MODES } from "../../../../components/compounds/graphics/components/ContainerOfChart"
import { CLIENT_SPRINT_CARD_MODES } from "../../../../consts/modules/clientReport.consts"
import { numberFormatter } from "../../../../utils/numbers.utils"

const MilestoneFlagLabel = ({ viewBox, value }) => {
  if (!viewBox || !value) return null

  const x = (viewBox?.x || 0) - 1
  const y = Math.max((viewBox?.y || 0) - 42, 0)
  const text = String(value)
  const fontSize = 12
  const padLeft = 8
  const padRight = 0
  const padY = 7
  const charWidth = fontSize * 0.62
  const width = Math.ceil(text.length * charWidth + padLeft + padRight)
  const height = fontSize + padY * 2
  const flagPath =
    "M0 0 H" +
    width +
    " V" +
    (height - 8) +
    " Q" +
    width +
    " " +
    height +
    " " +
    (width - 8) +
    " " +
    height +
    " H0 Z"

  return (
    <g transform={`translate(${x}, ${y})`}>
      <path d={flagPath} fill="#006AA6" />
      <text
        x={padLeft}
        y={height / 2}
        fill="#FFFFFF"
        fontSize={fontSize}
        fontFamily="Mulish"
        dominantBaseline="middle"
      >
        {text}
      </text>
    </g>
  )
}

const MilestoneHorizontalLabel = ({ viewBox, value }) => {
  if (!viewBox || !value) return null

  const x = (viewBox?.x || 0) + 8
  const y = Math.max((viewBox?.y || 0) - 8, 10)

  return (
    <text x={x} y={y} fill="#5F6B7A" fontSize="14" fontFamily="Cairo">
      {value}
    </text>
  )
}

const ClientReportBurnchart = ({
  data = [],
  estimatedSprints = {},
  emptyMessageTitle = "Sin información",
  emptyMessageMessage = "No hay proyecciones disponibles para el cliente y periodo seleccionados.",
  lineStyleData,
  loading,
  mode,
  milestone,
}) => {
  const isWeeklyData = useMemo(() => {
    if (!data || data.length === 0) return false
    return data[0]?.weekNumber !== undefined
  }, [data])

  const periodKey = isWeeklyData ? "weekNumber" : "sprintNumber"
  const periodLabel = isWeeklyData ? "Semanas" : "Sprints"
  const periodLabelSingular = isWeeklyData ? "Semana" : "Sprint"
  const formattedData = useMemo(() => {
    let dataToFormat = data
    if (data.length > 2000) {
      const commonDivisor = gcdMultiple(...Object.values(estimatedSprints))
      if (commonDivisor <= 15) {
        dataToFormat = data.filter((_, index) => index % commonDivisor === 0)
      }
    }
    return dataToFormat.map((item) => ({
      lineData: {
        Total: item.totalPointsToComplete,
        Projected: item.projectedNominalTotalCompletedPoints,
        ProjectedPessimistic: item.projectedPessimisticTotalCompletedPoints,
        ProjectedOptimistic: item.projectedOptimisticTotalCompletedPoints,
        Completed: item.totalCompletedPoints,
      },
      [periodKey]: item[periodKey],
      isMilestone: item.isMilestone || false,
      milestoneType: item.milestoneType || null,
      isEndPoint: item.isEndPoint || false,
      endPointType: item.endPointType || null,
      extraInfo: {
        Total: {
          isProjected: false,
          [periodKey]: item[periodKey],
        },
        Completed: {
          isProjected: false,
          [periodKey]: item[periodKey],
        },
        Projected: {
          isProjected: true,
          [periodKey]: item[periodKey],
          isMilestone: item.isMilestone || false,
          milestoneType: item.milestoneType || null,
        },
        ProjectedPessimistic: {
          isProjected: true,
          [periodKey]: item[periodKey],
          isMilestone: item.isMilestone || false,
          milestoneType: item.milestoneType || null,
        },
        ProjectedOptimistic: {
          isProjected: true,
          [periodKey]: item[periodKey],
          isMilestone: item.isMilestone || false,
          milestoneType: item.milestoneType || null,
        },
      },
    }))
  }, [data])

  const getTotalPointsToComplete = () => {
    const realSprints = data.filter(
      (sprint) =>
        !Object.keys(sprint).includes("projectedNominalTotalCompletedPoints") ||
        sprint.projectedNominalTotalCompletedPoints ===
          sprint.totalCompletedPoints
    )
    const maxTotalPointsToComplete = data.reduce((acc, item) => {
      const value = item.totalPointsToComplete
      return value > acc ? value : acc
    }, -Infinity)
    const customMax = data.find(
      (item) =>
        item[periodKey] === realSprints[realSprints.length - 1]?.[periodKey] + 1
    )?.totalPointsToComplete
    const max = customMax || maxTotalPointsToComplete
    return max
  }
  const getCustomYAxisDomain = (data) => {
    const maxTotalPointsToComplete = data.reduce((acc, item) => {
      const value = item.totalPointsToComplete
      return value > acc ? value : acc
    }, -Infinity)

    const milestonePoints = Number(milestone?.totalPoints)
    const maxMilestonePoints =
      Number.isFinite(milestonePoints) ? milestonePoints : -Infinity

    const maxValue = Math.max(maxTotalPointsToComplete, maxMilestonePoints)

    return [0, Math.ceil(maxValue / 10) * 10]
  }

  const lastRealPeriod = useMemo(() => {
    const realSprints = data.filter(
      (item) =>
        !Object.keys(item).includes("projectedNominalTotalCompletedPoints") ||
        item.projectedNominalTotalCompletedPoints === item.totalCompletedPoints
    )
    return realSprints[realSprints.length - 1]?.[periodKey]
  }, [data, periodKey])

  const customDotTemplate = (data, rechartsData) => {
    const { key, cx, cy, value } = rechartsData
    const maxTotalPointsToComplete = getTotalPointsToComplete()
    const extraInfo = rechartsData?.payload?.extraInfo?.[rechartsData?.dataKey]
    const isMilestone = extraInfo?.isMilestone || false
    const milestoneType = extraInfo?.milestoneType || null

    const isMarkedEndPoint = rechartsData?.payload?.isEndPoint || false
    const endPointType = rechartsData?.payload?.endPointType || null

    // Punto final de líneas proyectadas
    const isEndPoint =
      isMarkedEndPoint &&
      ((endPointType === "nominal" && data?.lineLabel === "Projected") ||
        (endPointType === "optimistic" &&
          data?.lineLabel === "ProjectedOptimistic") ||
        (endPointType === "pessimistic" &&
          data?.lineLabel === "ProjectedPessimistic"))

    // Punto milestone en su línea correspondiente
    const isMilestonePoint =
      isMilestone &&
      data?.lineLabel?.toLowerCase()?.includes("projected") &&
      ((milestoneType === "nominal" && data?.lineLabel === "Projected") ||
        (milestoneType === "optimistic" &&
          data?.lineLabel === "ProjectedOptimistic") ||
        (milestoneType === "pessimistic" &&
          data?.lineLabel === "ProjectedPessimistic"))

    // ✅ Nuevo: último punto de la línea real (Completed)
    const currentPeriod = Number(rechartsData?.payload?.[periodKey])
    const isLastRealCompletedPoint =
      data?.lineLabel === "Completed" &&
      Number.isFinite(currentPeriod) &&
      Number.isFinite(Number(lastRealPeriod)) &&
      currentPeriod === Number(lastRealPeriod)

    // Solo dibujar si es uno de los puntos especiales
    const shouldDrawPoint =
      isEndPoint ||
      isMilestonePoint ||
      isLastRealCompletedPoint ||
      (data?.lineLabel?.toLowerCase()?.includes("projected") &&
        value >= maxTotalPointsToComplete)

    if (!shouldDrawPoint) {
      return null
    }

    let pointColor = data?.color
    let tooltipLabel = ""

    if (isLastRealCompletedPoint) {
      pointColor = "var(--cyan-600)"
      tooltipLabel = `Cierre real: ${periodLabelSingular} ${numberFormatter(currentPeriod, 0)}`
    } else if (isMilestonePoint) {
      if (milestoneType === "nominal") {
        pointColor = "var(--primary-color-highlight)"
        tooltipLabel = `Fin proyectado del alcance de fase: ${periodLabelSingular} ${numberFormatter(rechartsData?.payload?.[periodKey], 1)}`
      } else if (milestoneType === "optimistic") {
        pointColor = "var(--green-600)"
        tooltipLabel = `Fin optimista del alcance de fase: ${periodLabelSingular} ${numberFormatter(rechartsData?.payload?.[periodKey], 1)}`
      } else if (milestoneType === "pessimistic") {
        pointColor = "var(--red-600)"
        tooltipLabel = `Fin pesimista del alcance de fase: ${periodLabelSingular} ${numberFormatter(rechartsData?.payload?.[periodKey], 1)}`
      }
    } else {
      if (data?.lineLabel === "ProjectedPessimistic") {
        tooltipLabel = `Fin pesimista: ${periodLabelSingular} ${numberFormatter(extraInfo?.[periodKey], 0)}`
      } else if (data?.lineLabel === "ProjectedOptimistic") {
        tooltipLabel = `Fin optimista: ${periodLabelSingular} ${numberFormatter(extraInfo?.[periodKey], 0)}`
      } else {
        tooltipLabel = `Fin proyectado: ${periodLabelSingular} ${numberFormatter(extraInfo?.[periodKey], 0)}`
      }
    }

    const pointType =
      isLastRealCompletedPoint ? "real-end"
      : isMilestonePoint ? "milestone"
      : "end"
    const dotClassName = `${rechartsData?.dataKey}-${pointType}-${key}`

    const dotRadius = isLastRealCompletedPoint ? 4 : 5
    const dotStroke = isLastRealCompletedPoint ? pointColor : "white"
    const dotStrokeWidth = isLastRealCompletedPoint ? 0 : 2

    const showTooltip = !isLastRealCompletedPoint
    return (
      <>
        <circle
          key={key}
          className={dotClassName}
          cx={cx}
          cy={cy}
          r={dotRadius}
          stroke={dotStroke}
          strokeWidth={dotStrokeWidth}
          fill={pointColor}
          opacity={lineStyleData?.[data?.lineLabel]?.opacity || 1}
        />
        {showTooltip && (
          <Tooltip target={`.${dotClassName}`} position="top">
            {tooltipLabel}
          </Tooltip>
        )}
      </>
    )
  }

  const getLineStrokeDash = ({ lineLabel }) => {
    if (lineLabel.toLowerCase().includes("projected")) {
      return "3 3" // Dashed line
    }
    return undefined
  }

  const milestoneReferenceLinesX = useMemo(() => {
    const periodNumber = Number(milestone?.periodNumber)
    if (!Number.isFinite(periodNumber)) return []

    const releaseLabel =
      milestone?.nextImportantDateName ||
      milestone?.milestoneName ||
      "Fecha importante"

    return [
      {
        x: periodNumber,
        stroke: "#006AA6",
        strokeWidth: 2,
        label: {
          position: "top",
          value: releaseLabel,
          content: <MilestoneFlagLabel />,
        },
      },
    ]
  }, [milestone])

  const milestoneReferenceLinesY = useMemo(() => {
    const totalPoints = Number(milestone?.totalPoints)
    if (!Number.isFinite(totalPoints)) return []

    return [
      {
        y: totalPoints,
        stroke: "#8A38F5",
        strokeWidth: 2,
        label: (
          <MilestoneHorizontalLabel
            value={milestone?.milestoneName || "Alcance de fase"}
          />
        ),
      },
    ]
  }, [milestone])

  if (!data.length && !loading) {
    return (
      <EmptyDataMessage
        title={emptyMessageTitle}
        message={emptyMessageMessage}
      />
    )
  }
  return (
    <>
      <LineChart
        key={formattedData}
        responsiveContainerConfig={{
          minWidth: "200px",
          minHeight: "320px",
          maxHeight: "320px",
        }}
        chartContainerConfig={{
          shadow: false,
          className: "h-full",
        }}
        data={formattedData}
        dataKey={periodKey}
        margin={{ top: 20, bottom: 5, left: 10, right: 50 }}
        areaConfig={{
          enabledLines: ["Total"],
          fillOpacity: 0.06, // 6%
          fillColor: "#306681",
        }}
        colors={{
          Total: "rgba(25, 81, 108, 0.25)",
          Completed: "var(--cyan-600)",
          Projected: "var(--primary-color-highlight)",
          ProjectedPessimistic: "var(--red-600)",
          ProjectedOptimistic: "var(--green-600)",
        }}
        lineStyle={lineStyleData}
        yAxiesTicksStep={Math.max(
          1,
          Math.ceil(getCustomYAxisDomain(data)[1] / 4)
        )}
        yAxisDomain={getCustomYAxisDomain(data)}
        labelAxiesConfig={{
          x: {
            value: periodLabel,
            position: "insideBottom",
            className: "font-bold m-5",
            margin: "0 0 10px 0",
            fill: "var(--text-color)",
            height: 60,
            dy: -10,
            type: "number",
            allowDecimals: false,
            tickCount: Math.min(formattedData.length, 15) + 1,
          },
          y: {
            value: "Pts",
            position: "top",
            className: "font-bold",
            fill: "var(--text-color)",
            dy: -5,
            padding: { top: 15 },
            allowDecimals: false,
          },
        }}
        yAxisAutoDomain={false}
        cartesian={false}
        tooltip
        tooltipTemplate={() => null}
        lineConfig={{
          type: "linear",
          dotTemplate: customDotTemplate,
          activeDotTemplate: customDotTemplate,
          strokeDashfun: getLineStrokeDash,
        }}
        brushConfig={{
          className: "custom-brush-hide-labels",
        }}
        brush={mode === CLIENT_SPRINT_CARD_MODES.EDITABLE}
        loading={loading}
        loadingMode={CONTAINER_OF_CHART_LOADING_MODES.SPINNER}
        xAxisFormatter={(value) => Math.round(value)}
        yAxisFormatter={(value) => Math.round(value)}
        xAxisTickStyle={{
          fontSize: "12px",
          fontFamily: "Mulish",
          fill: "var(--text-color)",
        }}
        yAxisTickStyle={{
          fontSize: "12px",
          fontFamily: "Mulish",
          fill: "var(--text-color)",
        }}
        referenceLinesX={milestoneReferenceLinesX}
        referenceLinesY={milestoneReferenceLinesY}
      />
    </>
  )
}

export default ClientReportBurnchart
